import {Injectable} from '@angular/core';
import {TranslateService} from "@ngx-translate/core";

const MAX_UNWRAP_DEPTH = 6;

function tryParseJson(input: string): unknown {
  try {
    return JSON.parse(input);
  } catch {
    return undefined;
  }
}

function unwrapTranslationScalar(raw: string): unknown {
  let value = raw.trim();
  if (!value) {
    return null;
  }

  for (let depth = 0; depth < MAX_UNWRAP_DEPTH; depth++) {
    if (value === 'null' || value === '{}' || value === '""' || value === "''") {
      return null;
    }

    const parsed = tryParseJson(value);
    if (parsed !== undefined) {
      if (parsed === null) {
        return null;
      }
      if (typeof parsed === 'string') {
        value = parsed.trim();
        continue;
      }
      if (typeof parsed === 'object' && !Array.isArray(parsed)) {
        return Object.keys(parsed as object).length === 0 ? null : parsed;
      }
      return parsed;
    }

    const unquoted = value.replace(/^["']+|["']+$/g, '').trim();
    if (unquoted !== value) {
      value = unquoted;
      continue;
    }

    return value;
  }

  return value;
}

function resolveLocalizationObject(raw: unknown): Record<string, unknown> | null {
  if (raw == null) {
    return null;
  }

  let resolved: unknown = raw;

  if (typeof raw === 'string') {
    resolved = unwrapTranslationScalar(raw);
  }

  if (!resolved || typeof resolved !== 'object' || Array.isArray(resolved)) {
    return null;
  }

  let obj = {...(resolved as Record<string, unknown>)};

  if (typeof obj['valuesJson'] === 'string') {
    const parsedValuesJson = tryParseJson(obj['valuesJson']);
    if (parsedValuesJson && typeof parsedValuesJson === 'object' && !Array.isArray(parsedValuesJson)) {
      const merged = {...(parsedValuesJson as Record<string, unknown>)};
      for (const [key, value] of Object.entries(obj)) {
        if (key !== 'valuesJson') {
          merged[key] = value;
        }
      }
      obj = merged;
    }
  }

  delete obj['valuesJson'];
  return obj;
}

export function isEmptyTranslationValue(value: unknown): boolean {
  if (value == null) {
    return true;
  }

  if (typeof value === 'string') {
    const unwrapped = unwrapTranslationScalar(value);
    if (unwrapped == null) {
      return true;
    }
    if (typeof unwrapped === 'string') {
      const trimmed = unwrapped.trim();
      return trimmed === '' || trimmed === 'null' || trimmed === '{}';
    }
    return isEmptyTranslationValue(unwrapped);
  }

  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.length === 0 || value.every(item => isEmptyTranslationValue(item));
    }

    const resolved = resolveLocalizationObject(value);
    if (!resolved) {
      return true;
    }

    const entries = Object.values(resolved);
    return entries.length === 0 || entries.every(item => isEmptyTranslationValue(item));
  }

  return false;
}

@Injectable({
  providedIn: 'root'
})
export class ServerTranslateService {

  constructor(private translateService: TranslateService) {
  }

  public isEmptyTranslationValue(value: unknown): boolean {
    return isEmptyTranslationValue(value);
  }

  public formatTranslationLabel(value: unknown): string {
    if (isEmptyTranslationValue(value)) {
      return '';
    }

    if (typeof value === 'string') {
      const unwrapped = unwrapTranslationScalar(value);
      if (unwrapped == null || isEmptyTranslationValue(unwrapped)) {
        return '';
      }
      return typeof unwrapped === 'string' ? unwrapped : '';
    }

    if (Array.isArray(value)) {
      const first = value.find(item => !isEmptyTranslationValue(item));
      return first != null ? String(first) : '';
    }

    if (typeof value === 'object' && value !== null) {
      const resolved = resolveLocalizationObject(value);
      if (!resolved) {
        return '';
      }

      const first = Object.values(resolved)
        .find(item => typeof item === 'string' && !isEmptyTranslationValue(item));
      if (typeof first === 'string') {
        const unwrapped = unwrapTranslationScalar(first);
        return typeof unwrapped === 'string' ? unwrapped : '';
      }
    }

    return '';
  }

  public isValidDisplayLabel(label: string | null | undefined): boolean {
    if (!label) {
      return false;
    }

    const trimmed = label.trim();
    if (!trimmed || trimmed === 'null') {
      return false;
    }

    return !isEmptyTranslationValue(trimmed);
  }

  public getDisplayValue(source: any, keyBase: string): string {
    if (!source || !keyBase) {
      return '';
    }

    const translated = this.translate(source, keyBase);
    if (translated != null && !isEmptyTranslationValue(translated)) {
      return this.formatTranslationLabel(translated);
    }

    return this.formatTranslationLabel(source[keyBase]);
  }

  public translate(value: any, keyBase: string): string | null | string[] {
    if (!value || !keyBase) return Array.isArray(value) ? [] : null;

    const currentLang = this.translateService.currentLang || this.translateService.defaultLang || 'en-US';
    const langKey = currentLang === 'ar-LB' ? 'ar-LB' : currentLang;

    const localizationObj = resolveLocalizationObject(value[keyBase]);
    if (!localizationObj || isEmptyTranslationValue(localizationObj)) {
      return null;
    }

    const directValue = localizationObj[langKey];
    if (typeof directValue === 'string' && !isEmptyTranslationValue(directValue)) {
      const unwrapped = unwrapTranslationScalar(directValue);
      return typeof unwrapped === 'string' ? unwrapped : null;
    }

    const fallbackLanguages = ['ru-RU', 'en-US', 'uz-Latn-UZ', 'uz-Cyrl-UZ', 'ar-LB'];
    for (const lang of fallbackLanguages) {
      const fallbackValue = localizationObj[lang];
      if (typeof fallbackValue === 'string' && !isEmptyTranslationValue(fallbackValue)) {
        const unwrapped = unwrapTranslationScalar(fallbackValue);
        return typeof unwrapped === 'string' ? unwrapped : null;
      }
    }

    return null;
  }

  /** Resolves a single language value (e.g. en-US for crop color keys) without UI locale fallback. */
  public translateForLanguage(value: any, keyBase: string, langKey: string): string | null {
    const localizationObj = resolveLocalizationObject(value[keyBase]);
    if (!localizationObj || isEmptyTranslationValue(localizationObj)) {
      return null;
    }

    for (const lang of [langKey, 'en-US', 'en']) {
      const localizedValue = localizationObj[lang];
      if (typeof localizedValue === 'string' && !isEmptyTranslationValue(localizedValue)) {
        const unwrapped = unwrapTranslationScalar(localizedValue);
        return typeof unwrapped === 'string' ? unwrapped : null;
      }
    }

    return null;
  }
}
